import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Send, Video, Phone, Lock, MessageCircle, Clock,
  CheckCircle2, XCircle, CreditCard, Stethoscope, User, FileText, Loader2, Download,
  Search, SlidersHorizontal, ShieldCheck, ChevronRight, Paperclip,
} from "lucide-react";
import LanguageToggle from "@/components/LanguageToggle";
import jsPDF from "jspdf";
import { useWebRTC } from "@/hooks/useWebRTC";
import CallOverlay from "@/components/CallOverlay";
import { registerWatchedConv, unregisterWatchedConv } from "@/utils/patientCallGuard";
import {
  getChatUnlockedUntil,
  getConsultationAccess,
  getConsultationExpiry,
} from "@/utils/consultationAccess";
import { toast } from "sonner";

interface Conversation {
  id: string;
  practitioner_id: string;
  practitioner_type: string;
  status: string;
  paid: boolean;
  consultation_started_at?: string | null;
  consultation_expires_at?: string | null;
  chat_unlocked_until?: string | null;
  last_message?: string;
  last_message_at?: string;
  fee: number;
  practitioner?: { full_name: string };
  pendingRequest?: {
    id: string;
    message_text?: string | null;
    request_type: string;
    status: string;
  };
}

interface Message {
  id: string;
  text: string;
  sender_id: string;
  created_at: string;
  message_type: string;
}

const cleanPrescriptionText = (text: string) => text.replace(/^📋\s*/, "");

const PatientInbox = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const bn = language === "bn";
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeChat, setActiveChat] = useState<Conversation | null>(null);
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [patientFullName, setPatientFullName] = useState("");
  const [inboxSearch, setInboxSearch] = useState("");
  const [inboxFilter, setInboxFilter] = useState<"all" | "active" | "completed">("all");
  const [, setAccessClock] = useState(0);

  const { callState, callType, isMuted, isCameraOff, isSpeakerOn, incomingCall, localStream, remoteStream,
    startCall, answerCall, rejectCall, endCall, toggleMute, toggleCamera, toggleSpeaker } = useWebRTC({
    conversationId: activeChat?.id ?? null,
    userId: user?.id ?? null,
    userName: user?.name || patientFullName || (bn ? "রোগী" : "Patient"),
  });

  // Tell PatientCallListener not to intercept calls for this conversation
  // while the patient has the chat open here — prevents double-handling
  useEffect(() => {
    const id = activeChat?.id;
    if (!id) return;
    registerWatchedConv(id);
    return () => unregisterWatchedConv(id);
  }, [activeChat?.id]);

  useEffect(() => {
    if (!activeChat) return;
    const timer = window.setInterval(() => setAccessClock((value) => value + 1), 30_000);
    return () => window.clearInterval(timer);
  }, [activeChat]);

  const downloadPrescription = async (msg: Message, _conversationId: string, practitionerName: string) => {
    if (!user) return;

    const { data: prescriptions, error } = await supabase
      .from("prescriptions")
      .select("*")
      .eq("patient_id", user.id)
      .order("created_at", { ascending: false });

    if (error || !prescriptions || prescriptions.length === 0) {
      const { toast } = await import("sonner");
      toast.error(bn ? "প্রেসক্রিপশন পাওয়া যায়নি" : "Prescription not found");
      return;
    }

    const msgTime = new Date(msg.created_at).getTime();
    const prescription = prescriptions.reduce((closest: any, p: any) => {
      const diff = Math.abs(new Date(p.created_at).getTime() - msgTime);
      const closestDiff = Math.abs(new Date(closest.created_at).getTime() - msgTime);
      return diff < closestDiff ? p : closest;
    });

    const isDoctor = activeChat?.practitioner_type === "doctor";
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = margin;

    const line = (text: string, size = 10, style: "normal" | "bold" = "normal", color: [number, number, number] = [30, 30, 30]) => {
      doc.setFontSize(size);
      doc.setFont("helvetica", style);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, contentW);
      lines.forEach((l: string) => {
        if (y > 270) { doc.addPage(); y = margin; }
        doc.text(l, margin, y);
        y += size * 0.45;
      });
      y += 2;
    };

    const hRule = () => {
      doc.setDrawColor(180, 180, 180);
      doc.line(margin, y, pageW - margin, y);
      y += 4;
    };

    doc.setFillColor(34, 139, 60);
    doc.rect(0, 0, pageW, 20, "F");
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("SEBACO", margin, 13);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(isDoctor ? "Doctor's Prescription" : "Nutrition Plan", margin + 36, 13);
    y = 28;

    line(`${isDoctor ? "Doctor" : "Dietitian"}: ${practitionerName}`, 10, "bold");
    line(`Patient: ${patientFullName}`, 10);
    line(`Date: ${new Date(prescription.created_at).toLocaleDateString()}   Duration: ${prescription.duration_days} days`, 9, "normal", [90, 90, 90]);
    y += 2;
    hRule();

    const sections = prescription.sections ?? [];
    sections.forEach((section: any) => {
      line(section.label, 10, "bold", [34, 139, 60]);
      (section.items ?? []).filter((i: any) => i.item?.trim()).forEach((item: any) => {
        const timing = item.timing === "before_meal" ? " (Before meal)" : item.timing === "after_meal" ? " (After meal)" : "";
        line(`  • ${item.item}${timing}`, 9);
      });
      y += 1;
    });

    if (prescription.special_notes?.trim()) {
      hRule();
      line("Special Notes", 10, "bold", [180, 120, 0]);
      line(prescription.special_notes, 9);
      y += 2;
    }

    y += 6;
    hRule();
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(130, 130, 130);
    doc.text("Generated by Sebaco Health Platform", margin, y);

    doc.save(`prescription-${patientFullName.replace(/\s+/g, "-")}-${new Date(prescription.created_at).toISOString().slice(0, 10)}.pdf`);
  };

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("full_name").eq("user_id", user.id).single()
      .then(({ data }) => { if (data) setPatientFullName(data.full_name ?? ""); });
  }, [user]);

  const loadMessages = async (conversationId: string) => {
    setMessagesLoading(true);
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at");
    if (error) {
      toast.error(error.message);
      setMessages([]);
    } else {
      setMessages(data ?? []);
    }
    setMessagesLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadError(null);
      const { data: convData, error } = await supabase
        .from("conversations")
        .select("*")
        .eq("patient_id", user.id)
        .order("last_message_at", { ascending: false });

      if (error || !convData) {
        setLoadError(error?.message ?? "Could not load conversations");
        setLoading(false);
        return;
      }

      const [requestResponse, practitionerResponse] = await Promise.all([
        supabase
          .from("consultation_requests")
          // Keep this query compatible with databases that have not yet received
          // the consultation-chat migration. The conversation is also present
          // in the conversations table, so it is not required to render inbox.
          .select("id, patient_id, practitioner_id, message_text, request_type, status")
          .eq("patient_id", user.id)
          .eq("status", "pending"),
        supabase
          .from("profiles")
          .select("user_id, full_name, fee")
          .in("user_id", [...new Set(convData.map((c) => c.practitioner_id))]),
      ]);
      if (requestResponse.error || practitionerResponse.error) {
        setLoadError(requestResponse.error?.message ?? practitionerResponse.error?.message ?? "Could not load consultation details");
        setLoading(false);
        return;
      }
      const requestData = requestResponse.data;
      const practitionerIds = [...new Set(convData.map((c) => c.practitioner_id))];
      const profileData = practitionerResponse.data;

      const profileMap: Record<string, { full_name: string; fee: number }> = {};
      (profileData ?? []).forEach((p) => {
        profileMap[p.user_id] = { full_name: p.full_name ?? "", fee: p.fee ?? 0 };
      });
      const pendingRequestMap: Record<string, Conversation["pendingRequest"]> = {};
       (requestData ?? []).forEach((request) => {
         const conversation = convData.find(
           (conversation) =>
             conversation.patient_id === request.patient_id &&
             conversation.practitioner_id === request.practitioner_id,
         );
         if (conversation) {
           pendingRequestMap[conversation.id] = {
             ...request,
             conversation_id: conversation.id,
           } as Conversation["pendingRequest"];
         }
       });

      setConversations(convData.map((c) => ({
        ...c,
        fee: profileMap[c.practitioner_id]?.fee ?? c.fee ?? 0,
        practitioner: { full_name: profileMap[c.practitioner_id]?.full_name ?? "" },
        pendingRequest: pendingRequestMap[c.id],
      })) as any);
      setLoading(false);
    };
    load();

    const refreshOnFocus = () => { void load(); };
    window.addEventListener("focus", refreshOnFocus);
    const channel = supabase
      .channel(`patient-inbox-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "conversations", filter: `patient_id=eq.${user.id}` }, refreshOnFocus)
      .on("postgres_changes", { event: "*", schema: "public", table: "consultation_requests", filter: `patient_id=eq.${user.id}` }, refreshOnFocus)
      .subscribe();
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      void supabase.removeChannel(channel);
    };
  }, [user]);

  const openChat = async (conv: Conversation) => {
    if (conv.status !== "accepted") {
      setActiveChat(conv);
      await loadMessages(conv.id);
      return;
    }

    let resolvedConv = conv;
    if (!conv.paid) {
      if (conv.fee <= 0) {
        toast.error(bn ? "এই ফ্রি পরামর্শটি এখনো সক্রিয় হয়নি" : "This free consultation is not active yet");
        return;
      }
      navigate("/payment", { state: { conversation: { ...conv, practitionerName: conv.practitioner?.full_name, fee: conv.fee } } });
      return;
    }

    setActiveChat(resolvedConv);
    await loadMessages(resolvedConv.id);
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !activeChat || !user) return;
    const access = getConsultationAccess(activeChat);
    const text = messageText.trim();

    if (access === "locked" || access === "expired") {
      if (activeChat.pendingRequest) {
        toast.info(bn ? "আপনার অনুরোধ এখনো অপেক্ষমাণ" : "Your request is still pending");
        return;
      }
      const { data: requestId, error } = await supabase.rpc("submit_follow_up_request", {
        _conversation_id: activeChat.id,
        _message_text: text,
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      const pendingRequest = {
        id: requestId,
        request_type: "follow_up",
        status: "pending",
        message_text: text,
      };
      const updated = { ...activeChat, pendingRequest };
      setActiveChat(updated);
      setConversations(prev => prev.map(c => c.id === updated.id ? updated : c));
      setMessageText("");
      toast.success(bn ? "ফলো-আপ অনুরোধ পাঠানো হয়েছে" : "Follow-up request sent");
      return;
    }

    if (access !== "open") return;
    const { data, error } = await supabase.rpc("send_consultation_message", {
      _conversation_id: activeChat.id,
      _text: text,
      _message_type: "text",
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data) setMessages(prev => [...prev, data]);
    setMessageText("");
  };

  const visibleConversations = conversations.filter((conversation) => {
    const query = inboxSearch.trim().toLowerCase();
    const practitionerName = conversation.practitioner?.full_name?.toLowerCase() ?? "";
    const matchesSearch = !query || practitionerName.includes(query) || (conversation.last_message ?? "").toLowerCase().includes(query);
    const completed = conversation.status === "completed" || conversation.status === "rejected";
    const matchesFilter = inboxFilter === "all" || (inboxFilter === "completed" ? completed : !completed);
    return matchesSearch && matchesFilter;
  });

  const statusBadge = (status: string) => {
    switch (status) {
      case "accepted":
        return <span className="flex items-center gap-1 rounded-full bg-[#e3f2ec] px-2 py-1 text-[10px] font-bold text-[#2e806d]"><CheckCircle2 className="h-2.5 w-2.5" /> {bn ? "গৃহীত" : "Accepted"}</span>;
      case "pending":
        return <span className="flex items-center gap-1 rounded-full bg-[#fff3d9] px-2 py-1 text-[10px] font-bold text-[#a87824]"><Clock className="h-2.5 w-2.5" /> {bn ? "অপেক্ষমান" : "Pending"}</span>;
      case "rejected":
        return <span className="flex items-center gap-1 rounded-full bg-[#fbe9e8] px-2 py-1 text-[10px] font-bold text-destructive"><XCircle className="h-2.5 w-2.5" /> {bn ? "প্রত্যাখ্যাত" : "Rejected"}</span>;
      case "completed":
        return <span className="flex items-center gap-1 rounded-full bg-[#edf0ef] px-2 py-1 text-[10px] font-bold text-[#687672]"><CheckCircle2 className="h-2.5 w-2.5" /> {bn ? "সম্পন্ন" : "Completed"}</span>;
    }
    return null;
  };

  if (activeChat) {
    const access = getConsultationAccess(activeChat);
    const canChat = access === "open";
    const canViewMessages = activeChat.status === "accepted" && activeChat.paid;
    const practitionerName = activeChat.practitioner?.full_name ?? "";

    return (
      <div className="fixed inset-0 z-[60] flex flex-col bg-background">
        <div className="flex shrink-0 items-center gap-2 border-b border-[#e2ebe7] bg-white px-4 py-3">
          <button onClick={() => setActiveChat(null)} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#eef7f4] text-[#367f78]">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e1f2ed]">
            {activeChat.practitioner_type === "doctor" ? <Stethoscope className="h-5 w-5 text-[#398b82]" /> : <User className="h-5 w-5 text-[#398b82]" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-extrabold text-[#244c47]">{practitionerName}</p>
            <p className="text-[10px] text-[#788783]">{canChat ? (bn ? "অনলাইন" : "Online") : ""}</p>
          </div>
          <div className="flex items-center gap-1">
              <button className="flex h-9 w-9 items-center justify-center rounded-full text-[#398b82] hover:bg-[#e1f2ed] disabled:opacity-50" disabled={!canChat || callState !== "idle"} onClick={() => startCall("audio")}>
                <Phone className="h-4 w-4" />
              </button>
              <button className="flex h-9 w-9 items-center justify-center rounded-full text-[#398b82] hover:bg-[#e1f2ed] disabled:opacity-50" disabled={!canChat || callState !== "idle"} onClick={() => startCall("video")}>
                <Video className="h-4 w-4" />
              </button>
          </div>
        </div>

        {activeChat.status === "accepted" && !activeChat.paid && (
          <div className="flex-1 flex items-center justify-center p-6">
            <motion.div className="glass rounded-2xl p-6 shadow-float text-center max-w-sm" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="w-16 h-16 rounded-full bg-warning/20 mx-auto flex items-center justify-center mb-4"><Lock className="h-8 w-8 text-warning" /></div>
              <h3 className="font-extrabold text-base mb-2">{bn ? "পেমেন্ট প্রয়োজন" : "Payment Required"}</h3>
              <p className="text-sm text-muted-foreground mb-4">{bn ? "মেসেজ ও ভিডিও কল করতে পেমেন্ট করুন" : "Pay to unlock messaging & video calling"}</p>
              <p className="text-2xl font-extrabold text-primary mb-4">৳{activeChat.fee}</p>
              <motion.button onClick={() => navigate("/payment", { state: { conversation: { ...activeChat, practitionerName, fee: activeChat.fee } } })}
                className="w-full gradient-primary text-primary-foreground py-3.5 rounded-xl font-bold text-sm shadow-glow flex items-center justify-center gap-2" whileTap={{ scale: 0.97 }}>
                <CreditCard className="h-4 w-4" />{bn ? "পেমেন্ট করুন" : "Pay Now"}
              </motion.button>
            </motion.div>
          </div>
        )}

        {activeChat.status === "pending" && (
          <div className="flex-1 flex items-center justify-center p-6">
            <motion.div className="glass rounded-2xl p-6 shadow-float text-center max-w-sm" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="w-16 h-16 rounded-full bg-warning/20 mx-auto flex items-center justify-center mb-4"><Clock className="h-8 w-8 text-warning" /></div>
              <h3 className="font-extrabold text-base mb-2">{bn ? "অনুরোধ পাঠানো হয়েছে" : "Request Sent"}</h3>
              <p className="text-sm text-muted-foreground">{bn ? "আপনার অনুরোধ গ্রহণ করলে আপনাকে জানানো হবে" : "You'll be notified when your request is accepted"}</p>
            </motion.div>
          </div>
        )}

        {activeChat.status === "rejected" && (
          <div className="flex-1 flex items-center justify-center p-6">
            <motion.div className="glass rounded-2xl p-6 shadow-float text-center max-w-sm" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="w-16 h-16 rounded-full bg-destructive/20 mx-auto flex items-center justify-center mb-4"><XCircle className="h-8 w-8 text-destructive" /></div>
              <h3 className="font-extrabold text-base mb-2">{bn ? "অনুরোধ প্রত্যাখ্যাত" : "Request Declined"}</h3>
              <p className="text-sm text-muted-foreground">{bn ? "অন্য একজন খুঁজুন।" : "Try another one."}</p>
              <button onClick={() => navigate("/find-dietitian")} className="mt-4 gradient-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold text-sm shadow-glow">
                {bn ? "অন্য খুঁজুন" : "Find Another"}
              </button>
            </motion.div>
          </div>
        )}

        {access === "locked" && (
          <div className="px-5 pt-4">
            <div className="rounded-2xl border border-warning/30 bg-warning/10 p-4">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-warning mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-extrabold">
                    {bn ? "চ্যাট সাময়িকভাবে বন্ধ" : "Chat is temporarily locked"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {activeChat.pendingRequest
                      ? (bn ? "ডাক্তারের উত্তরের অপেক্ষায়" : "Waiting for the practitioner to respond")
                      : (bn ? "চালিয়ে যেতে একটি ফলো-আপ অনুরোধ পাঠান" : "Send a follow-up request to continue")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {access === "expired" && (
          <div className="px-5 pt-4">
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                 <div className="flex-1">
                  <p className="text-sm font-extrabold">
                    {bn ? "১৫ দিনের পরামর্শ শেষ হয়েছে" : "15-day consultation expired"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {bn ? "চ্যাট লক হয়েছে। পুনরায় খুলতে নিচে একটি অনুরোধ পাঠান। ডাক্তার গ্রহণ করলে পেমেন্টের পর চ্যাট ও ভিডিও কল আবার চালু হবে।" : "Messaging is locked. Send a reopening request below. After the doctor accepts, payment will reopen chat and video calling."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {canViewMessages && (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto bg-[#f2faf9] px-5 py-4">
              {messagesLoading && (
                <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              )}
              {messages.map((msg) => {
                const isPatient = msg.sender_id === user?.id;
                return (
                  <motion.div key={msg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${isPatient ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-[23px] border px-4 py-3 ${isPatient ? "rounded-tr-[8px] border-[#d5e7e2] bg-[#e7f4f1] text-[#244c47]" : "rounded-tl-[8px] border-[#e1ebe8] bg-white text-[#244c47]"}`}>
                      {msg.message_type === "prescription" && (
                        <div className="mb-1 flex items-center gap-2 text-[#398b82]"><FileText className="h-4 w-4" /><span className="sr-only">{bn ? "প্রেসক্রিপশন" : "Prescription"}</span></div>
                      )}
                      <p className="text-sm">{msg.message_type === "prescription" ? cleanPrescriptionText(msg.text) : msg.text}</p>
                      {msg.message_type === "prescription" && !isPatient && (
                        <button
                          onClick={() => downloadPrescription(msg, activeChat.id, practitionerName)}
                          className="mt-2 flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                          <Download className="h-3 w-3" />
                          {bn ? "PDF ডাউনলোড" : "Download PDF"}
                        </button>
                      )}
                      <p className="mt-1 text-[10px] text-[#788783]">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <div className="sticky bottom-0 border-t border-[#e2ebe7] bg-white p-3">
                {(access === "locked" || access === "expired") && activeChat.pendingRequest ? (
                  <div className="rounded-xl bg-warning/10 px-4 py-3 text-center text-xs font-bold text-warning">
                    {bn ? "পুনরায় খোলার অনুরোধ অপেক্ষমাণ" : "Reopening request pending"}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="flex h-11 w-9 items-center justify-center text-[#398b82]" aria-hidden="true">
                      <Paperclip className="h-5 w-5" />
                    </span>
                    <input
                      type="text"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                      placeholder={access === "locked" || access === "expired"
                        ? (bn ? "পুনরায় খোলার অনুরোধ লিখুন..." : "Write a reopening request...")
                        : (bn ? "মেসেজ লিখুন..." : "Type a message...")}
                      className="flex-1 rounded-full border border-[#e1ebe8] bg-[#f7fbfa] px-4 py-3 text-sm font-medium text-foreground outline-none placeholder:text-[#8a9693] focus:border-[#6aafa5] focus:ring-0"
                    />
                    <motion.button onClick={sendMessage} className="flex h-12 w-12 items-center justify-center rounded-full bg-[#63aaa1] text-white" whileTap={{ scale: 0.92 }}>
                      <Send className="h-4 w-4" />
                    </motion.button>
                  </div>
                )}
            </div>
          </>
        )}

        <CallOverlay
          callState={callState}
          callType={callType}
          partnerName={activeChat.practitioner?.full_name ?? ""}
          isMuted={isMuted}
          isCameraOff={isCameraOff}
          isSpeakerOn={isSpeakerOn}
          incomingCall={incomingCall}
          localStream={localStream}
          remoteStream={remoteStream}
          onAnswer={answerCall}
          onReject={rejectCall}
          onEnd={endCall}
          onToggleMute={toggleMute}
          onToggleCamera={toggleCamera}
          onToggleSpeaker={toggleSpeaker}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center px-5 pb-28">
        <div className="glass max-w-sm rounded-2xl p-6 text-center shadow-float">
          <p className="font-extrabold text-destructive">{bn ? "ইনবক্স লোড করা যায়নি" : "Could not load inbox"}</p>
          <p className="mt-2 text-xs text-muted-foreground">{loadError}</p>
          <button onClick={() => window.location.reload()} className="mt-4 w-full rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground">{bn ? "আবার চেষ্টা করুন" : "Try again"}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfcf8] pb-28">
      <section className="relative min-h-[254px] overflow-hidden rounded-b-[34px] bg-[#237e76] px-5 pb-4 pt-5">
        <div className="relative z-20 flex justify-end">
          <LanguageToggle />
        </div>
        <div className="relative mt-5 min-h-[148px]">
          <h1 className="relative z-10 font-serif text-[30px] font-extrabold leading-tight text-white">
            {bn ? "ইনবক্স" : "Inbox"}
          </h1>
          <p className="relative z-10 mt-2 max-w-[220px] text-sm leading-5 text-white/90">
            {bn ? "আপনার পরামর্শ কথোপকথন" : "Your consultation conversations"}
          </p>
          <img
            src="/inbox-messages.png"
            alt=""
            className="pointer-events-none absolute -bottom-10 -right-8 h-[190px] w-[285px] object-contain object-right-bottom"
          />
        </div>
      </section>

      <div className="space-y-5 px-5 pt-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#62736f]" />
          <input
            type="text"
            value={inboxSearch}
            onChange={(event) => setInboxSearch(event.target.value)}
            placeholder={bn ? "কথোপকথন খুঁজুন..." : "Search conversations..."}
            className="w-full rounded-full border border-[#e1e9e4] bg-white py-3.5 pl-11 pr-12 text-sm font-medium text-foreground outline-none placeholder:text-[#8b9894] focus:border-[#2c8979]"
          />
          <SlidersHorizontal className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#168c74]" />
        </div>

        <div className="flex gap-2">
          {([
            ["all", bn ? "সব" : "All"],
            ["active", bn ? "সক্রিয়" : "Active"],
            ["completed", bn ? "সম্পন্ন" : "Completed"],
          ] as const).map(([filter, label]) => (
            <button
              key={filter}
              type="button"
              onClick={() => setInboxFilter(filter)}
              className={`flex-1 rounded-full border py-2.5 text-xs font-bold transition-colors ${
                inboxFilter === filter
                  ? "border-[#237e76] bg-[#237e76] text-white"
                  : "border-[#e1e9e4] bg-white text-[#3e4f4b]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div>
          <h2 className="font-serif text-base font-extrabold text-[#243b37]">{bn ? "আপনার কথোপকথন" : "Your conversations"}</h2>
          <p className="mt-1 text-xs text-[#788783]">{bn ? "আপনার পরামর্শ চ্যাট দেখুন এবং চালিয়ে যান" : "View and continue your consultation chats"}</p>
        </div>

        <div className="space-y-3">
          {visibleConversations.map((conv, i) => (
            <motion.button
              key={conv.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => openChat(conv)}
              className="w-full rounded-[18px] border border-[#e2ebe4] bg-white p-4 text-left"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e1f1eb] text-[#166f62]">
                  {conv.practitioner_type === "doctor" ? <Stethoscope className="h-6 w-6" /> : <User className="h-6 w-6" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-extrabold text-[#233a36]">{conv.practitioner?.full_name ?? ""}</p>
                        <span className="rounded-full bg-[#e1f1eb] px-1.5 py-0.5 text-[8px] font-bold text-[#2e796a]">
                          {conv.practitioner_type === "doctor" ? (bn ? "ডাক্তার" : "Doctor") : (bn ? "ডায়েটিশিয়ান" : "Dietitian")}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-[#788783]">
                        {conv.practitioner_type === "doctor" ? (bn ? "ডাক্তারের পরামর্শ" : "Doctor consultation") : (bn ? "পুষ্টি পরামর্শ" : "Nutrition consultation")}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span className="text-[10px] text-[#65736f]">{conv.last_message_at ? new Date(conv.last_message_at).toLocaleDateString() : ""}</span>
                      {conv.status === "accepted" && <span className="h-2 w-2 rounded-full bg-[#237e76]" />}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {statusBadge(conv.status)}
                    {conv.status === "accepted" && !conv.paid && (
                      <span className="flex items-center gap-1 rounded-full bg-[#e1f1eb] px-2 py-1 text-[10px] font-bold text-[#2e806d]">
                        <CreditCard className="h-2.5 w-2.5" /> {bn ? "পেমেন্ট করুন" : "Pay"}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 truncate text-xs text-[#3e4f4b]">
                        <FileText className="h-3.5 w-3.5 shrink-0 text-[#6e817b]" />
                        {conv.last_message || (bn ? "কথোপকথন শুরু করুন" : "Start a conversation")}
                      </p>
                      {conv.last_message_at && <p className="ml-5 mt-1 text-[10px] text-[#788783]">{new Date(conv.last_message_at).toLocaleDateString()}</p>}
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-[#243b37]" />
                  </div>
                </div>
              </div>
            </motion.button>
          ))}

          {visibleConversations.length === 0 && (
            <div className="py-16 text-center">
              <MessageCircle className="mx-auto mb-3 h-12 w-12 text-[#9aa9a4]" />
              <p className="text-sm text-muted-foreground">
                {conversations.length === 0
                  ? (bn ? "কোনো কথোপকথন নেই" : "No conversations yet")
                  : (bn ? "কোনো মিল পাওয়া যায়নি" : "No matching conversations")}
              </p>
            </div>
          )}
        </div>

        <div className="mx-auto mt-36 flex max-w-xs items-center gap-3 rounded-2xl bg-[#e8f4ef] px-4 py-3 text-[#60746e]">
          <ShieldCheck className="h-6 w-6 shrink-0 text-[#287c6d]" />
          <p className="text-xs leading-5">
            {bn ? "আপনার কথোপকথন নিরাপদ এবং সুরক্ষিত।" : "Your conversations are secure and protected with end-to-end encryption."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PatientInbox;